import React from "react";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import dayjs from "dayjs";
import ActionFilter from "../../components/ActionFilter";
import CommonPagination from "../../components/Table/CommonPagination";
import { limitChar } from "../../utils/common";

const headerCellStyles = {
  fontSize: {
    xs: "14px",
    sm: "15px",
    md: "15px",
    lg: "16px",
    xl: "16px",
  },
  color: "#000",
  fontWeight: 600,
  padding: {
    xs: "10px 5px",
    sm: "15px 10px",
    md: "15px 10px",
    lg: "20px 15px",
    xl: "20px 15px",
  },
  backgroundColor: "#e0e0e0",
};

const bodyCellStyles = {
  fontSize: {
    xs: "12px",
    sm: "13px",
    md: "14px",
    lg: "15px",
    xl: "15px",
  },
  color: "#333",
  padding: {
    xs: "10px 5px",
    sm: "10px",
    md: "10px",
    lg: "10px 15px",
    xl: "10px 15px",
  },
};

const StatusChip = ({ contest }) => {
  const today = dayjs();
  const startDate = dayjs(contest.startDate);
  const endDate = dayjs(contest.endDate);
  const votingStartDate = dayjs(contest.votingStartDate);
  const votingEndDate = dayjs(contest.votingEndDate);

  const submissionInProgress =
    today.isAfter(startDate) && today.isBefore(endDate);
  const votingInProgress =
    today.isAfter(votingStartDate) && today.isBefore(votingEndDate);
  const hasPassed =
    endDate.isBefore(today) &&
    votingEndDate.isBefore(today) &&
    !contest.isEnded;

  const label = contest.isDeleted
    ? "Deleted"
    : contest.isEnded
    ? "Ended"
    : submissionInProgress
    ? "Submission"
    : votingInProgress
    ? "Voting"
    : hasPassed
    ? "Passed"
    : "Upcoming";

  if (contest.isDeleted || hasPassed) {
    return (
      <Chip
        label={label}
        variant="outlined"
        color="error"
        sx={{
          "& .MuiChip-label": {
            fontSize: "12px",
            padding: "0 12px",
          },
        }}
      />
    );
  }

  if (submissionInProgress || votingInProgress) {
    return (
      <Chip
        label={label}
        sx={{
          backgroundColor: "#4CAF50",
          color: "white",
          "& .MuiChip-label": {
            fontSize: "12px",
            padding: "0 12px",
          },
        }}
      />
    );
  }

  if (contest.isEnded || today.isBefore(startDate)) {
    return (
      <Chip
        label={label}
        variant="outlined"
        sx={{
          border: "1px solid #2196F3",
          color: "#2196F3",
          "& .MuiChip-label": {
            fontSize: "12px",
            padding: "0 12px",
          },
        }}
      />
    );
  }

  return null;
};

export default function ContestTable({
  classes,
  contests,
  isAdmin,
  limit,
  totalContests,
  page,
  onPageChange,
  onView,
  onEdit,
  onDelete,
}) {
  return (
    <Box
      sx={{
        padding: "15px 15px 0 15px",
        borderRadius: "6px",
        boxShadow:
          "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
      }}
    >
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow className={classes.stickyHeader}>
              <TableCell sx={headerCellStyles}>Logo</TableCell>
              <TableCell sx={headerCellStyles}>Title</TableCell>
              <TableCell sx={headerCellStyles}>Prize</TableCell>
              <TableCell sx={headerCellStyles}>Start Date</TableCell>
              <TableCell sx={headerCellStyles}>End Date</TableCell>
              <TableCell sx={headerCellStyles}>Status</TableCell>
              {isAdmin && (
                <>
                  <TableCell sx={headerCellStyles}>
                    Number of Submissions
                  </TableCell>
                  <TableCell sx={headerCellStyles}>Number of rounds</TableCell>
                  <TableCell sx={headerCellStyles}>
                    Duration per round
                  </TableCell>
                </>
              )}
              <TableCell sx={headerCellStyles}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contests.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isAdmin ? 9 : 6}
                  sx={{
                    ...bodyCellStyles,
                    textAlign: "center",
                    height: "calc(100vh - 280px)",
                  }}
                >
                  No Contests found
                </TableCell>
              </TableRow>
            ) : (
              contests.map((challenge, index) => (
                <TableRow key={challenge?._id}>
                  <TableCell sx={bodyCellStyles}>
                    {challenge?.logo ? (
                      <img
                        src={challenge?.logo}
                        alt="Category Logo"
                        width="25px"
                        style={{ backgroundColor: "transparent" }}
                      />
                    ) : (
                      <span>No Logo</span>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellStyles}>
                    {challenge.title ? limitChar(challenge?.title) : ""}
                  </TableCell>
                  <TableCell sx={bodyCellStyles}>
                    {challenge.prize
                      ? limitChar(challenge?.prize, 30)
                      : "No Prize"}
                  </TableCell>
                  <TableCell sx={bodyCellStyles}>
                    {dayjs(challenge?.startDate).format("DD MMM YYYY")}
                  </TableCell>
                  <TableCell sx={bodyCellStyles}>
                    {dayjs(challenge.endDate).format("DD MMM YYYY")}
                  </TableCell>
                  <TableCell sx={bodyCellStyles}>
                    <StatusChip contest={challenge} />
                  </TableCell>
                  {isAdmin && (
                    <>
                      <TableCell sx={bodyCellStyles}>
                        {challenge?.numberOfSubmissions}
                      </TableCell>
                      <TableCell sx={bodyCellStyles}>
                        {challenge?.estimatedNumberOfRounds}
                      </TableCell>
                      <TableCell sx={bodyCellStyles}>
                        {challenge?.estimatedDurationOfEachRound}
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={bodyCellStyles}>
                    <ActionFilter
                      onView={onView}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      index={index}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CommonPagination
        rowsPerPage={limit}
        count={totalContests}
        page={page}
        onPageChange={onPageChange}
      />
    </Box>
  );
}
